/**
 * Notification Context
 *
 * Provides a centralized notification system for the application.
 * Handles Autopilot notifications (signals, run completions) and general alerts.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { autopilotService, AutopilotSignal, AutopilotRun, SignalSeverity } from '../services/autopilotService';
import { useShouldPaywall } from './TrialContext';
import { isActionAwaitingDecision } from '../utils/autopilotActions';
import { markdownToPlainText } from '../components/issues/AiFixMarkdown';

// =============================================================================
// Types
// =============================================================================

export type NotificationType =
  | 'autopilot_signal'      // New signal detected
  | 'autopilot_run_complete' // Autopilot run finished
  | 'autopilot_action_ready' // Action ready for approval
  | 'system_alert'          // System-level alerts
  | 'success'               // Success messages
  | 'warning'               // Warning messages
  | 'error';                // Error messages

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  dismissed: boolean;
  severity?: SignalSeverity;
  metadata?: {
    projectId?: string;
    signalId?: string;
    runId?: string;
    actionId?: string;
    link?: string;
    /** autopilot_action_ready: how many actions are ready. */
    actionCount?: number;
  };
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read' | 'dismissed'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismissNotification: (id: string) => void;
  clearAll: () => void;
  loadAutopilotNotifications: (projectId: string) => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

// =============================================================================
// Storage Key
// =============================================================================

const STORAGE_KEY = 'rnkrocket_notifications';
const MAX_NOTIFICATIONS = 50;

// =============================================================================
// Provider
// =============================================================================

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.map((n: Notification) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
      }
    } catch (e) {
      console.error('[NotificationContext] Failed to load notifications from storage:', e);
    }
    return [];
  });

  // Persist to localStorage when notifications change
  useEffect(() => {
    try {
      // Only store non-dismissed notifications
      const toStore = notifications.filter(n => !n.dismissed).slice(0, MAX_NOTIFICATIONS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (e) {
      console.error('[NotificationContext] Failed to save notifications:', e);
    }
  }, [notifications]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read && !n.dismissed).length;

  // Add a new notification
  const addNotification = useCallback((
    notification: Omit<Notification, 'id' | 'timestamp' | 'read' | 'dismissed'>
  ) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
      dismissed: false
    };

    setNotifications(prev => {
      // Check for duplicate (same type and metadata within last 5 minutes)
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      const isDuplicate = prev.some(n =>
        n.type === newNotification.type &&
        n.title === newNotification.title &&
        n.timestamp > fiveMinutesAgo &&
        JSON.stringify(n.metadata) === JSON.stringify(newNotification.metadata)
      );

      if (isDuplicate) {
        return prev;
      }

      // Add to front, limit total
      return [newNotification, ...prev].slice(0, MAX_NOTIFICATIONS);
    });
  }, []);

  // Mark a single notification as read
  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
  }, []);

  // Dismiss a notification (removes from visible list)
  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, dismissed: true } : n)
    );
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Load autopilot notifications for a project
  const loadAutopilotNotifications = useCallback(async (projectId: string) => {
    try {
      // Fetch recent signals and runs
      const [insightsRes, historyRes] = await Promise.all([
        autopilotService.getInsights(projectId, { suppressUpgradeToast: true }),
        autopilotService.getHistory(projectId, 10, { suppressUpgradeToast: true })
      ]);

      if (insightsRes.success && insightsRes.data) {
        // Add notifications for high-priority signals
        const highPrioritySignals = insightsRes.data.signals.filter(
          (s: AutopilotSignal) => s.severity === 'critical' || s.severity === 'high'
        );

        highPrioritySignals.forEach((signal: AutopilotSignal) => {
          addNotification({
            type: 'autopilot_signal',
            title: getSignalTitle(signal),
            // WB-43: a notification line is plain text, not literal Markdown.
            message: markdownToPlainText(signal.payload.description),
            severity: signal.severity,
            metadata: {
              projectId,
              signalId: signal.id,
              link: '/autopilot'
            }
          });
        });

        // Add notifications for actions awaiting a decision. Same rule as the
        // Radar and the Autopilot page: 'ready' and 'generated' (WB-122).
        const readyActions = insightsRes.data.actions.filter(isActionAwaitingDecision);
        if (readyActions.length > 0) {
          addNotification({
            type: 'autopilot_action_ready',
            title: `${readyActions.length} Action${readyActions.length > 1 ? 's' : ''} Ready`,
            message: `You have ${readyActions.length} SEO action${readyActions.length > 1 ? 's' : ''} ready for review.`,
            metadata: {
              projectId,
              link: '/autopilot',
              actionCount: readyActions.length
            }
          });
        }
      }

      if (historyRes.success && historyRes.data) {
        // Add notification for most recent completed run
        const recentRun = historyRes.data.runs.find((r: AutopilotRun) => r.status === 'completed');
        if (recentRun) {
          const runDate = new Date(recentRun.completed_at || recentRun.started_at);
          const hoursSinceRun = (Date.now() - runDate.getTime()) / (1000 * 60 * 60);

          // Only notify if run completed within last 24 hours
          if (hoursSinceRun < 24) {
            // WB-117: this read "Found 1 signals and generated 1 actions".
            const signals = recentRun.summary.signals_detected;
            const actions = recentRun.summary.actions_generated;
            addNotification({
              type: 'autopilot_run_complete',
              title: 'Autopilot Scan Complete',
              message: `Found ${signals} signal${signals === 1 ? '' : 's'} and generated ${actions} action${actions === 1 ? '' : 's'}.`,
              metadata: {
                projectId,
                runId: recentRun.id,
                link: '/autopilot'
              }
            });
          }
        }
      }
    } catch (error) {
      console.error('[NotificationContext] Failed to load autopilot notifications:', error);
    }
  }, [addNotification]);

  // WB-113: approving Autopilot actions is paid-only (Autopilot locks every
  // action for an unpaid trial), so for a trial the "N Actions Ready" notice
  // says the actions are locked rather than "ready for review". Mapped at
  // display time, so stored notices and ones added while trial state was
  // still loading read correctly too. Fails open, like every paywall hint.
  const showPaywall = useShouldPaywall();
  const visibleNotifications = useMemo(() => {
    const visible = notifications.filter(n => !n.dismissed);
    return showPaywall ? visible.map(asLockedActionNotice) : visible;
  }, [notifications, showPaywall]);

  const value: NotificationContextValue = {
    notifications: visibleNotifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    loadAutopilotNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

// =============================================================================
// Helpers
// =============================================================================

/** WB-113: an unpaid trial's reading of an "actions ready" notice. */
function asLockedActionNotice(n: Notification): Notification {
  if (n.type !== 'autopilot_action_ready') return n;
  // Older stored notices carry the count only in their title ("2 Actions Ready").
  const count = n.metadata?.actionCount ?? Number.parseInt(n.title, 10);
  if (!Number.isFinite(count) || count < 1) {
    return {
      ...n,
      title: 'Actions Ready (Locked)',
      message: 'Autopilot has SEO actions for your site. Approving actions is on paid plans, so upgrade to see and approve them.'
    };
  }
  const one = count === 1;
  return {
    ...n,
    title: `${count} Action${one ? '' : 's'} Ready (Locked)`,
    message: `Autopilot has ${count} SEO action${one ? '' : 's'} for your site. Approving actions is on paid plans, so upgrade to see and approve ${one ? 'it' : 'them'}.`
  };
}

function getSignalTitle(signal: AutopilotSignal): string {
  const typeLabels: Record<string, string> = {
    'content_decay': 'Content Decay Detected',
    'missing_entities': 'Missing Entities Found',
    'competitor_new_page': 'Competitor Published New Content',
    'ranking_drop': 'Ranking Drop Alert',
    'thin_content': 'Thin Content Detected',
    'stale_content': 'Stale Content Alert',
    'keyword_gap': 'Keyword Gap Opportunity',
    'internal_linking_gap': 'Internal Linking Opportunity'
  };
  return typeLabels[signal.type] || signal.payload.title;
}
